import React from 'react';
import { TbCoin, TbCoins } from 'react-icons/tb';
import { motion } from 'framer-motion';

/**
 * AIPriceBadge Component
 * 
 * Displays AI model pricing with a coin icon
 * Handles both per-generation and per-character/per-second pricing
 * 
 * @param {Object} props
 * @param {number} props.price - Base price per unit
 * @param {number} props.quantity - Number of units (e.g., characters, seconds, generations)
 * @param {string} props.priceType - Type of pricing: 'generation', 'character', 'second'
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {string} props.variant - Color variant: 'default', 'purple', 'green', 'pink', 'blue'
 * @param {boolean} props.animate - Whether to animate the badge
 * @param {string} props.className - Additional CSS classes
 */
const AIPriceBadge = ({ 
    price = 0, 
    quantity = 1, 
    priceType = 'generation',
    size = 'md',
    variant = 'default',
    animate = true,
    className = ''
}) => {
    // Calculate total price
    const totalPrice = Math.ceil(price * quantity);
    
    // Size configurations
    const sizeClasses = {
        sm: {
            wrapper: 'px-2 py-0.5 gap-1',
            icon: 'text-xs',
            text: 'text-xs'
        },
        md: {
            wrapper: 'px-2.5 py-1 gap-1.5',
            icon: 'text-sm',
            text: 'text-sm'
        },
        lg: {
            wrapper: 'px-3 py-1.5 gap-2',
            icon: 'text-base',
            text: 'text-base'
        }
    };
    
    // Variant configurations
    const variantClasses = {
        default: 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30',
        purple: 'bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-500/30',
        green: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
        pink: 'bg-pink-500/20 text-pink-700 dark:text-pink-400 border-pink-500/30',
        blue: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
        white: 'bg-white/20 text-white dark:text-white border-white/30'
    };
    
    const sizeConfig = sizeClasses[size] || sizeClasses.md;
    const variantClass = variantClasses[variant] || variantClasses.default;
    
    // Format price text
    const getPriceText = () => {
        if (priceType === 'character' || priceType === 'second') {
            return (
                <>
                    <span className="font-bold">{totalPrice}</span>
                    {/* <span className="opacity-70 font-normal">
                        {quantity > 1 && ` (${price}×${quantity})`}
                    </span> */}
                </>
            );
        }
        return <span className="font-bold">{totalPrice}</span>;
    };
    
    const BadgeContent = () => (
        <div className={`
            inline-flex items-center justify-center
            ${sizeConfig.wrapper}
            ${variantClass}
            border rounded-full font-semibold
            backdrop-blur-sm
            transition-all duration-200
            ${className}
        `}>
            <TbCoins className={`${sizeConfig.icon} flex-shrink-0`} />
            <span className={sizeConfig.text}>
                {getPriceText()}
            </span>
        </div>
    );
    
    if (animate) {
        return (
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="inline-block"
            >
                <BadgeContent />
            </motion.div>
        );
    }
    
    return <BadgeContent />;
};

export default AIPriceBadge;

